import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categoryLinks } from "@/mocks/kakobuy-data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur-md shadow-sm"
          : "bg-background-50/80 backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-3 group cursor-pointer">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <i className="ri-file-list-3-line text-background-50 text-lg md:text-xl"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900 whitespace-nowrap">
              Kakobuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-home-4-line mr-1 text-xs"></i>
              Home
            </Link>
            <Link to="/guide" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-book-open-line mr-1 text-xs"></i>
              Shopping Guide
            </Link>
            <div className="w-px h-5 bg-background-300"></div>
            <a href="#categories" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Categories</a>
            <a href="#trust" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Why Trust Us</a>
            <a href="#guides" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Articles</a>
            <a href="#faq" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">FAQ</a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Browse Now
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-700 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-2 max-h-[70vh] overflow-y-auto">
          <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm font-semibold text-foreground-800 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-home-4-line text-base"></i>
            Home
          </Link>
          <Link to="/guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-sm font-semibold text-foreground-800 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-book-open-line text-base"></i>
            Shopping Guide
          </Link>

          <div className="border-t border-background-200/70 my-1 pt-2">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-2 mb-1">Categories</p>
            <div className="grid grid-cols-2 gap-1">
              {categoryLinks.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 text-sm text-foreground-600 py-2 px-2 rounded-lg hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-background-200/70 pt-2 mt-1">
            <a href="#categories" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-foreground-600 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">Categories</a>
            <a href="#trust" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-foreground-600 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">Why Trust Us</a>
            <a href="#guides" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-foreground-600 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">Articles</a>
            <a href="#faq" onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-foreground-600 py-2.5 px-2 rounded-lg hover:bg-background-100 cursor-pointer">FAQ</a>
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-1.5 px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer mt-1"
          >
            Browse Now
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>
        </div>
      )}
    </nav>
  );
}